import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Heading,
  Stack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Modal,
  ModalOverlay,
  ModalContent,
  useToast,
  ModalHeader,
  Spinner,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useDisclosure,
  VStack,
  FormControl,
  FormLabel,
  Checkbox,
  useTheme,
  Spacer,
  useColorModeValue, Textarea, RadioGroup, Radio, HStack, FormErrorMessage, FormHelperText, Icon, Select
} from "@chakra-ui/react";

import EditableText from "../EditableText/EditableText";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { cqAPI, Auth } from "../../lib/Auth";
import dialogManager from "../DialogManager/DialogManager";
import ReactSelect, {components} from "react-select";
import moment from "moment";

import "./PhoneSettings.css"; // Import the CSS file
import { RiUploadCloud2Line } from "react-icons/ri";
import { IoMdCheckmark } from "react-icons/io";
import { PiWarningCircleBold } from "react-icons/pi";
import { DeleteIcon } from "@chakra-ui/icons";
import {formatPhoneNumber} from "../../lib/PhoneNumbers";

// ============================================================
// SET THE CALLER ID (CNAM): the legal data Twilio reviews before it lets us
// show the practice name on outgoing calls. The API tells us the allowed
// values, what is still missing and what to prefill, so nothing is hardcoded
// here beyond the field names themselves. (SS-4040)
// ============================================================
const CNAM_FIELDS = [
  "legal_name",
  "cnam_display_name",
  "business_type",
  "business_registration_identifier",
  "business_registration_number",
  "website_url",
];
const CNAM_REPRESENTATIVE_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "phone_number",
  "business_title",
  "job_position",
];
// Missing entries the practice cannot fix from this form — they are ours to sort out.
const CNAM_ACCOUNT_BLOCKERS = ["twilio_address_sid", "TWILIO_BUSINESS_PROFILE_SID"];

// ============================================================
// PORT-IN CARDS: a port-in card is a port IN FLIGHT. Once a request reaches one
// of these statuses it is over, and it must never be mistaken for a port that is
// still running — a completed one especially, whose number already has a number
// card of its own. (SS-4151)
// ============================================================
const FINAL_PORT_IN_STATUSES = ["Completed", "Canceled", "Port Rejected"];
// spelled as the API stores them (the PortInNumber status enum)
const PORTING_STATUSES = [
  "In Review",
  "Waiting for Signature",
  "Port Submitted",
  "Port Pending",
];
// A completed request has nothing left to offer, so its card is dropped here as
// well as by the API. A canceled or rejected one keeps its card: that is where
// "Delete Port In" and the retry live.
const isCompletedPortIn = (portInNum) => portInNum?.status === "Completed";
// A finished request keeps its port_in_request_sid forever, so counting one as
// pending on that alone pushed an office that started a NEW port for the same
// number into the "retry or cancel the pending port" modal — for a port that had
// finished weeks earlier.
export const pendingPortInNumbersFrom = (portInNumbers = []) =>
  portInNumbers
    .filter(
      (portInNum) =>
        !FINAL_PORT_IN_STATUSES.includes(portInNum.status) &&
        (PORTING_STATUSES.includes(portInNum.status) ||
          !!portInNum.port_in_request_sid)
    )
    .map((portInNum) => portInNum.phone_number);

const emptyCnamRepresentative = () =>
  CNAM_REPRESENTATIVE_FIELDS.reduce((acc, key) => ({ ...acc, [key]: "" }), {});

// Mirrors sanitizeCnamDisplayName in api/controllers/pstn.js so the field shows
// exactly what gets submitted. The API sanitizes again on save, so this only
// affects what the practice sees while typing.
const cnamDisplayNameFrom = (name) =>
  String(name || "")
    .replace(/&/g, " AND ")
    .replace(/[^a-zA-Z0-9., ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[^a-zA-Z]+/, "")
    .slice(0, 15)
    .trim()
    .toUpperCase();

// Same, but keeps a space the practice is still typing between words — without
// it the sanitizer's trim would swallow every space as it is typed.
const cnamDisplayNameWhileTyping = (value) => {
  const sanitized = cnamDisplayNameFrom(value);
  return /\s$/.test(value) && sanitized && sanitized.length < 15
    ? `${sanitized} `
    : sanitized;
};

// Everything already on file is shown as-is; `suggested` only fills a blank.
const cnamFormFromProfile = (data) => ({
  ...CNAM_FIELDS.reduce(
    (acc, key) => ({ ...acc, [key]: data[key] || data.suggested?.[key] || "" }),
    {}
  ),
  representative: {
    ...emptyCnamRepresentative(),
    ...(data.authorized_representatives?.[0] || {}),
  },
});

// True when the practice has answered this before, so the form asks it to
// confirm what we already know instead of typing it again.
const hasStoredCnamData = (data) =>
  CNAM_FIELDS.some((key) => data[key]) ||
  Boolean(data.authorized_representatives?.length);

// `missing` comes back as API paths (e.g.
// "twilio_customer_profile_data.authorized_representatives[0].email"); turn it
// into the form keys to highlight.
const cnamMissingKeys = (missing) => {
  const keys = new Set();
  (missing || []).forEach((entry) => {
    const path = entry.replace("twilio_customer_profile_data.", "");
    const representative = path.match(/^authorized_representatives\[0\](?:\.(.+))?$/);
    if (representative) {
      if (representative[1]) {
        keys.add(`representative.${representative[1]}`);
      } else {
        // No representative at all: every one of its fields is still needed.
        CNAM_REPRESENTATIVE_FIELDS.forEach((key) => keys.add(`representative.${key}`));
      }
      return;
    }
    if (CNAM_FIELDS.includes(path)) {
      keys.add(path);
    }
  });
  return keys;
};

function PhoneNumbers() {
  const theme = useTheme();
  const [phoneNumbersData, setPhoneNumbersData] = useState({
    phones: [],
    DNIs: [],
    empty_dnis: [],
    routings: [],
  });
  const [defaultRouting, setDefaultRouting] = useState("");
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState("");
  const [phoneNumerToPort, setPhoneNumerToPort] = useState("");

  // port in number(s)
  const [portInNumberObjs, setPortInNumberObjs] = useState([]);
  const [phoneNumbersToPort, setPhoneNumbersToPort] = useState("")
  const [portabilityStatuses, setPortabilityStatuses] = useState([]);
  const [pendingPortInNumbers, setPendingPortInNumbers] = useState([]);
  const [currentPendingNum, setCurrentPendingNum] = useState({});
  const [currentPendingIdx, setCurrentPendingIdx] = useState(0);
  const [portableNumbers, setPortableNumbers] = useState([]);
  const [portableNumbersWithPin, setPortableNumbersWithPin] = useState([]);
  const [pins,  setPins] = useState({});
  const [activePinIdx, setActivePinIdx] = useState(0);
  const [activePinPhone,  setActivePinPhone] = useState("");
  const [portInAccountType, setPortInAccountType] = useState("Business");
  const [portInCustomerName, setPortInCustomerName] = useState("");
  const [portInAuthRepresentative, setPortInAuthRepresentative] = useState("");
  const [portInAccountNumber, setPortInAccountNumber] = useState("");
  const [portInEmail, setPortInEmail] = useState("");
  const [portInEmailError, setPortInEmailError] = useState("");
  const [phoneBills, setPhoneBills] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
  const [twilioNumbers, setTwilioNumbers] = useState([]);

  const [selectedTags, setSelectedTags] = useState({});
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  // Held while a port-in support ticket is being opened, so the button cannot start a
  // second one, and the key that makes a retried request the same request.
  const [submittingPortInTicket, setSubmittingPortInTicket] = useState(false);
  const portInRequestKeyRef = useRef(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [poolSize, setPoolSize] = useState(1);
  const [currentPhone, setCurrentPhone] = useState(null);
  const [currentPhoneAreaCode, setCurrentPhoneAreaCode] = useState(null);
  const routingUpdateProgress = {};
  const headerBackgroundColor = useColorModeValue("#F1F1F5", "#303343");
  const backgroundColor = useColorModeValue("#FFFFFF", "#212330");
  const headerTextColor = useColorModeValue("#092728", "#F9F9FB");
  const hoverBackgroundColor = useColorModeValue("#212330", "#FFFFFF");
  const toast = useToast();

  // Modals
  const {
    isOpen: isConfigOpen,
    onOpen: onConfigOpen,
    onClose: onConfigClose,
  } = useDisclosure();

  const {
    isOpen: isBuyNumOpen,
    onOpen: onBuyNumOpen,
    onClose: onBuyNumClose,
  } = useDisclosure();

  const {
    isOpen: isPortInOpen,
    onOpen: onPortInOpen,
    onClose: onPortInClose,
  } = useDisclosure();

  const {
    isOpen: isPortInNumbersOpen,
    onOpen: onPortInNumbersOpen,
    onClose: onPortInNumbersClose,
  } = useDisclosure();

  const {
    isOpen: isPortInStatusOpen,
    onOpen: onPortInStatusOpen,
    onClose: onPortInStatusClose,
  } = useDisclosure()

  const {
    isOpen: isRetryPortInOpen,
    onOpen: onRetryPortOpen,
    onClose: onRetryPortInClose,
  } = useDisclosure();

  const {
    isOpen: isCreateSupportTicketOpen,
    onOpen: onCreateSupportTicketOpen,
    onClose: onCreateSupportTicketClose
  } = useDisclosure();

  const {
    isOpen: isTempNumberOpen,
    onOpen: onTempNumberOpen,
    onClose: onTempNumberClose,
  } = useDisclosure();

  const {
    isOpen: isPortInCallerIdOpen,
    onOpen: onPortInCallerIdOpen,
    onClose: onPortInCallerIdClose,
  } = useDisclosure();

  const {
    isOpen: isPortInAuthOpen,
    onOpen: onPortInAuthOpen,
    onClose: onPortInAuthClose,
  } = useDisclosure();

  const {
    isOpen: isUploadPhoneBillOpen,
    onOpen: onUploadPhoneBillOpen,
    onClose: onUploadPhoneBillClose,
  } = useDisclosure();

  const {
    isOpen: isPortInPinOpen,
    onOpen: onPortInPinOpen,
    onClose: onPortInPinClose,
  } = useDisclosure();

  const {
    isOpen: isSubmitPortInOpen,
    onOpen: onSubmitPortInOpen,
    onClose: onSubmitPortInClose,
  } = useDisclosure();

  // DNI modal
  const { isOpen, onOpen, onClose } = useDisclosure();

  // States for saving spinners
  const [isSavingPoolSize, setIsSavingPoolSize] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isSavingPort, setIsSavingPort] = useState(false);
  const [isCheckingPortability, setIsCheckingPortability] = useState(false);

  // Verification modals
  const {
    isOpen: isVerifyNumOpen,
    onOpen: onVerifyNumOpen,
    onClose: onVerifyNumClose,
  } = useDisclosure();

  const {
    isOpen: isVerifyCodeOpen,
    onOpen: onVerifyCodeOpen,
    onClose: onVerifyCodeCloseInner,
  } = useDisclosure();

  const [verifyPhoneNumber, setVerifyPhoneNumber] = useState("");
  const [verifyPhoneCode, setVerifyPhoneCode] = useState("");
  const [pollingIntervalId, setPollingIntervalId] = useState(null);

  // ============================================================
  // NEW: CallerID (List of verified phones) Modal
  // ============================================================
  const {
    isOpen: isCallerIdOpen,
    onOpen: onCallerIdOpen,
    onClose: onCallerIdClose,
  } = useDisclosure();

  const [verifiedPhones, setVerifiedPhones] = useState([]);
  const [isLoadingVerifiedPhones, setIsLoadingVerifiedPhones] = useState(false);
  const fileInputRef = useRef();

  const fetchVerifiedPhones = async () => {
    setIsLoadingVerifiedPhones(true);
    try {
      const resp = await cqAPI.get("/settings/phone/verify");
      if (resp?.type === "ok" && resp.data) {
        setVerifiedPhones(resp.data);
      }
    } catch (err) {
      console.error("Error fetching verified phones:", err);
    }
    setIsLoadingVerifiedPhones(false);
  };

  // Fetch all verified phones whenever callerID modal is opened
  useEffect(() => {
    if (isCallerIdOpen || isPortInCallerIdOpen) {
      fetchVerifiedPhones();
    }
  }, [isCallerIdOpen, isPortInCallerIdOpen]);

  /**
   * Toggle the callerID for a phone number:
   *   - If it's already selected => do a DELETE /settings/phone/callerid
   *   - If it's not selected => do a POST /settings/phone/callerid
   */
  const handleToggleCallerId = async (phone, currentlySelected) => {
    try {
      let resp = null;
      if (currentlySelected) {
        // If it's already selected, "unset" it
        resp = await cqAPI.del("/settings/phone/callerid", { phone });
      } else {
        // Otherwise, set it
        resp = await cqAPI.post("/settings/phone/callerid", { phone });
      }

      if (resp?.type === "ok") {
        toast({
          title: currentlySelected
            ? "Caller ID unset successfully!"
            : "Caller ID set successfully!",
          status: "success",
        });
        // Refresh to show updated 'selected' statuses
        fetchVerifiedPhones();
        fetchPhoneNumbers();
      } else if (resp?.type === "error") {
        toast({
          title: "Error",
          description: resp.msg,
          status: "error",
        });
      }
    } catch (error) {
      console.error("Error toggling caller ID:", error);
      toast({
        title: "Error",
        description: "An error occurred while toggling CallerID.",
        status: "error",
      });
    }
  };

  const handleDeleteVerifiedPhone = async (phone) => {
    // DELETE /settings/phone/verify
    const resp = await cqAPI.del("/settings/phone/verify", {
      phone,
    });
    if (resp?.type === "ok") {
      // remove from local list
      setVerifiedPhones((prev) => prev.filter((p) => p.phone !== phone));
      toast({
        title: "Verified number removed.",
        status: "success",
      });
    } else if (resp?.type === "error") {
      toast({
        title: "Error deleting the number",
        description: resp.msg,
        status: "error",
      });
    }
  };

  // ============================================================
  // FETCH & PREPARE PHONE NUMBERS
  // ============================================================
  const getNumberInfo = (phone, allcampaigns) => {
    const isDNI = "size" in phone;
    const _id = isDNI ? phone.sid : phone._id;
    const campaigns = allcampaigns.filter((c) =>
      c.numbers.find((n) => n.id === _id)
    );
    const name = phone.main_number
      ? "Main number"
      : campaigns.map((i) => i.name).join(",") || "No campaign";
    // The caller ID name is practice-level, so it lives in the page's Actions
    // menu rather than on a number's card. (SS-4040)
    return {
      isDNI,
      _id,
      main_number: phone.main_number,
      router: phone.router,
      campaigns,
      name,
      ...(isDNI
        ? {
            DNI: _id,
            areacode: phone.phone,
            exceptions: phone.exceptions,
            apply_exceptions_on_landing: phone.apply_exceptions_on_landing,
            phone_number: phone.phone_number,
          }
        : {
            sid: phone.sid,
          }),
      title: isDNI
        ? "DNI" + (phone.phone ? ` (${phone.phone})` : "")
        : phone.phone,
      calls: phone.calls || {},
      routing: phone.routing,
      size: phone.size,
      actions: isDNI
        ? phone.phone
          ? ["Release", "Change pool size", "Configure"]
          : ["Setup"]
        : phone.main_number
        ? (phoneNumbersData.emergency &&
          phoneNumbersData.emergency.emergencyAddressSid
            ? phoneNumbersData.emergency.emergencyStatus === "Active"
              ? ["911 is Active"].concat(Auth.user.isSuperAdmin? ['Release'] : [])
              : phoneNumbersData.emergency.emergencyStatus === "Inactive"
              ? ["911 is Inactive"]
              : [phoneNumbersData.emergency.emergencyStatus]
            : ["Emergency address registration pending ..."].concat(Auth.user.isSuperAdmin? ['Release'] : [])
          )
        : phoneNumbersData.is_root ? [(phone.router ? "Disable Location Selector" : "Enable Location Selector"), "Release"].concat(phone.router? [] : ["Make main"]) : ["Release", "Make main"],
    };
  };

  const fetchPhoneNumbers = async () => {
    try {
      const response = await cqAPI.get("/settings/phone/numbers");
      if (!response.data) {
        return;
      }
      setPhoneNumbersData(response.data);
      setDefaultRouting(response.data.routings.find((r) => r.default));
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
    }
  };

  useEffect(() => {
    fetchPhoneNumbers();
    getPortInNumbers();
  }, []);

  // ============================================================
  // Set the Caller ID (CNAM) Modal
  // ============================================================
  const {
    isOpen: isCnamOpen,
    onOpen: onCnamOpen,
    onClose: onCnamClose,
  } = useDisclosure();

  const [cnamProfile, setCnamProfile] = useState(null);
  const [cnamForm, setCnamForm] = useState(null);
  const [cnamMissing, setCnamMissing] = useState(new Set());
  const [cnamConfirming, setCnamConfirming] = useState(false);
  const [cnamLoadFailed, setCnamLoadFailed] = useState(false);
  // once the practice sets the caller ID name itself, it stops following the
  // legal name (a stored value counts as already set)
  const [cnamDisplayNameEdited, setCnamDisplayNameEdited] = useState(false);
  const [isLoadingCnam, setIsLoadingCnam] = useState(false);
  const [isSavingCnam, setIsSavingCnam] = useState(false);

  // Never show the previous visit's answers, whether the form is opened again
  // or the reload fails.
  const resetCnamForm = () => {
    setCnamProfile(null);
    setCnamForm(null);
    setCnamMissing(new Set());
    setCnamLoadFailed(false);
    setCnamDisplayNameEdited(false);
  };

  const fetchCnamProfile = async () => {
    setIsLoadingCnam(true);
    resetCnamForm();
    try {
      const resp = await cqAPI.get("/settings/phone/cnam");
      if (resp?.type === "ok" && resp.data) {
        setCnamProfile(resp.data);
        setCnamForm(cnamFormFromProfile(resp.data));
        setCnamConfirming(hasStoredCnamData(resp.data));
        setCnamDisplayNameEdited(Boolean(resp.data.cnam_display_name));
        setCnamMissing(new Set());
      } else {
        setCnamLoadFailed(true);
        toast({
          title: "Error loading the caller ID information",
          description: resp?.msg,
          status: "error",
        });
      }
    } catch (err) {
      console.error("Error fetching the caller ID information:", err);
      setCnamLoadFailed(true);
    }
    setIsLoadingCnam(false);
  };

  // Load what we already know every time the form is opened
  useEffect(() => {
    if (isCnamOpen) {
      fetchCnamProfile();
    }
  }, [isCnamOpen]);

  const updateCnamField = (key, value) =>
    setCnamForm((prev) => {
      const next = { ...prev, [key]: value };
      // Twilio reviews the caller ID name against the legal name, so it follows
      // that name until the practice adjusts it — otherwise a legal name typed
      // after the form loaded would leave a stale caller ID behind.
      if (key === "legal_name" && !cnamDisplayNameEdited) {
        next.cnam_display_name = cnamDisplayNameFrom(value);
      }
      return next;
    });

  const updateCnamRepresentative = (key, value) =>
    setCnamForm((prev) => ({
      ...prev,
      representative: { ...prev.representative, [key]: value },
    }));

  /**
   * Saves the form. The API accepts partial answers, so whatever is filled in
   * is kept and what is still missing comes back to be highlighted. Once
   * nothing is missing it starts the registration, and the action disappears.
   */
  const handleSaveCnamProfile = async () => {
    setIsSavingCnam(true);
    // The application is submitted for whichever number is main right now
    const submittedForSid = phoneNumbersData.phones?.find(
      (phone) => phone.main_number
    )?.sid;
    try {
      // The form edits the first representative; a second one (supplied through
      // the ops path) is not rendered but must not be silently dropped, so it is
      // round-tripped unchanged.
      const secondRepresentative = cnamProfile?.authorized_representatives?.[1];
      const resp = await cqAPI.put("/settings/phone/cnam", {
        ...CNAM_FIELDS.reduce((acc, key) => ({ ...acc, [key]: cnamForm[key] }), {}),
        // submitting without leaving the field must still send the exact value
        // the practice was shown, never a raw one the API would rewrite
        cnam_display_name:
          cnamDisplayNameFrom(cnamForm.cnam_display_name) ||
          cnamDisplayNameFrom(cnamForm.legal_name),
        authorized_representatives: [
          cnamForm.representative,
          ...(secondRepresentative ? [secondRepresentative] : []),
        ],
      });
      if (resp?.type === "ok" && resp.data) {
        setCnamProfile(resp.data);
        setCnamMissing(cnamMissingKeys(resp.data.missing));
        if (resp.data.complete && resp.data.error) {
          // Error first: `submitted` can be true because ANOTHER registration is
          // already running for this number — that must never read as "this
          // submission succeeded" (the number is not eligible, or the lock is
          // held; the form stays open so the practice can try again).
          toast({
            title: "The caller ID was not submitted",
            description: resp.data.error,
            status: "error",
          });
        } else if (resp.data.registration_started || resp.data.submitted) {
          toast({
            title: "Caller ID submitted.",
            description:
              "Your practice name is under review and starts showing on outgoing calls once it is approved.",
            status: "success",
          });
          onCnamClose();
          await fetchPhoneNumbers();
          // The registration is started in the background, so the list can
          // still report the number as not submitted for a moment (and the
          // refresh may not answer at all). The submission we just got back is
          // what decides whether the action is still offered — but only for
          // the number it was submitted for: if the main number changed in the
          // meantime, the new one needs its own application.
          setPhoneNumbersData((prev) => {
            const mainSid = prev.phones?.find((phone) => phone.main_number)?.sid;
            if (mainSid !== submittedForSid) {
              return prev;
            }
            return {
              ...prev,
              cnam: {
                ...prev.cnam,
                submitted: true,
                status: resp.data.status || prev.cnam?.status || null,
              },
            };
          });
        } else {
          toast({
            title: "Your answers were saved.",
            description: "Complete the highlighted fields to submit the caller ID.",
            status: "info",
          });
        }
      } else if (resp?.msg) {
        toast({
          title: "Error saving the caller ID information",
          description: resp.msg,
          status: "error",
        });
      }
    } catch (err) {
      console.error("Error saving the caller ID information:", err);
      toast({
        title: "Error saving the caller ID information",
        status: "error",
      });
    }
    setIsSavingCnam(false);
  };

  useEffect(() => {
    setCurrentPendingIdx(0);
    if (pendingPortInNumbers.length > 0) {
      setCurrentPendingNum(pendingPortInNumbers[0]);
    }
  }, [pendingPortInNumbers]);

  useEffect(() => {
    if (pendingPortInNumbers.length > currentPendingIdx) {
      setCurrentPendingNum(pendingPortInNumbers[currentPendingIdx]);
    }
  }, [currentPendingIdx]);

  useEffect(() => {
    setActivePinIdx(0);
    if (portableNumbersWithPin.length > 0) {
      setActivePinPhone(portableNumbersWithPin[0]);
    }
  }, [portableNumbersWithPin])

  useEffect(() => {
    if (portableNumbersWithPin.length > activePinIdx) {
      setActivePinPhone(portableNumbersWithPin[activePinIdx]);
    }
  }, [activePinIdx])

  useEffect(() => {
    try {
      const allPhoneNumbers = phoneNumbersData.phones
        .concat(
          phoneNumbersData.empty_dnis.map((x) => ({
            ...x,
            size: 0,
            sid: x._id,
          }))
        )
        .map((x) => getNumberInfo(x, phoneNumbersData.campaigns));
      setPhoneNumbers(allPhoneNumbers);

      // Initialize tags for each phone with corresponding routing plan
      const consolidatedOptions = allPhoneNumbers.reduce((acc, phone) => {
        acc[phone._id] = (phone.routing || []).map((id) => ({
          value: id,
          label: phoneNumbersData.routings.find((option) => option._id === id)
            ?.name,
        }));
        return acc;
      }, {});
      setSelectedTags(consolidatedOptions);
    } catch (error) {
      console.error("Error managing phone numbers:", error);
    }
  }, [phoneNumbersData]);

  // ============================================================
  // API POST HELPER
  // ============================================================
  const postAndHandle = (ep, body, nofetch) => {
    return cqAPI.post(ep, body).then((resp) => {
      if (!resp) {
        return resp;
      }
      if (resp.type === "ok" && resp.data && resp.data.makemain) {
        return postAndHandle("/settings/phone/makemain?confirm=1", {
          _id: resp.data?.number?._id,
        });
      }
      if (resp.type === "ok" && !nofetch) {
        return fetchPhoneNumbers();
      }
      if (resp.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: resp.msg,
          header: "Error",
        });
      }
      return resp;
    });
  };

  const handleSaveClick = async (phoneId, newValue) => {
    let updateCampaign;
    const updatedPhones = phoneNumbers.map((phone) => {
      if (phone._id === phoneId) {
        updateCampaign = { ...phone.campaigns[0], name: newValue };
        return { ...phone, name: newValue };
      }
      return phone;
    });
    setPhoneNumbers(updatedPhones);
    if (updateCampaign) {
      await postAndHandle("/settings/mt/campaign/update", {
        cid: updateCampaign._id,
        name: updateCampaign.name,
      });
      return true;
    }
    return false;
  };

  // ============================================================
  // SELECT STYLES
  // ============================================================
  const customStyles = {
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.brand[50],
      borderRadius: "16px",
      padding: "0px 6px",
      display: "flex",
      alignItems: "center",
      border: "1px solid #CBD5E0",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#2D3748",
      fontWeight: "500",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#2D3748",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "#E2E8F0",
        color: "#E53E3E",
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      display: "none",
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: backgroundColor,
      borderColor: theme.colors.gray[300],
      boxShadow: theme.shadows.sm,
      "&:hover": {
        borderColor: theme.colors.gray[400],
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.gray[50],
      zIndex: 2000,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 3000,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? theme.colors.brand[100]
        : theme.colors.gray[50],
      color: theme.colors.gray[700],
      "&:hover": {
        backgroundColor: theme.colors.gray[50],
      },
    }),
  };

  // ============================================================
  // DNI / BUY PHONE
  // ============================================================
  const handleOpenModal = (phone) => {
    setCurrentPhone(phone);
    setPoolSize(phone.size || 0);
    onOpen();
  };

  const handleOpenBuyNumModal = () => {
    setCurrentPhone({});
    setPoolSize(1);
    onBuyNumOpen();
  };

  const handlePoolSizeChange = (valueAsString, valueAsNumber) => {
    if (valueAsNumber >= 1 && valueAsNumber <= 10) {
      setPoolSize(valueAsNumber);
    } else if (valueAsNumber < 1) {
      setPoolSize(1);
    } else if (valueAsNumber > 10) {
      setPoolSize(10);
    }
  };

  const savePoolSize = async (single) => {
    if (single && (!currentPhone?._id && !currentCampaign)) {
      setAlertMessage("The source cannot be empty.");
      setIsAlertVisible(true);
      return;
    }
    if (poolSize < 1 || poolSize > 10) {
      setAlertMessage("Pool size has to be between 1 and 10.");
      setIsAlertVisible(true);
      return;
    }
    setIsSavingPoolSize(true);
    const areacode = currentPhone?.areacode || currentPhoneAreaCode;
    if (!/^\d{3}/.test(areacode)) {
      setAlertMessage("Please select the area code. It can only be a 3-digit number.");
      setIsAlertVisible(true);
      setIsSavingPoolSize(false);
      return;
    }
    const response = await cqAPI.get("/phone/lookup?areacode=" + areacode);
    setIsSavingPoolSize(false);
    if (!response) {
      onClose();
      onBuyNumClose();
      return;
    }
    if (response.msg) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    }

    const required_numbers = currentPhone?.size
      ? poolSize - currentPhone?.size
      : poolSize;

    if (response.data.length < required_numbers) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: "There are not enough numbers available in this area code for this DNI pool.",
        header: "Error",
      });
      return;
    }
    const numbers = response.data.splice(0, required_numbers);
    setIsSavingPoolSize(true);
    await postAndHandle("/phone/purchase", {
      phonenumber: numbers,
      campaign: currentCampaign,
      ...(single
        ? {}
        : {
            DNI: currentPhone?._id,
            pool: true,
          }),
    });
    setIsSavingPoolSize(false);
    onClose();
    onBuyNumClose();
    if (isTempNumberOpen) {
      onTempNumberClose();
      onPortInCallerIdOpen();
    }
    setCurrentCampaign("");
  };

  // ============================================================
  // CONFIGURE DNI
  // ============================================================
  const handleConfigOpenModal = (phone) => {
    setCurrentPhone(phone);
    onConfigOpen();
  };

  const handleSaveConfig = async () => {
    if (!validatePhoneNumber(currentPhone.phone_number)) {
      setAlertMessage("Please enter a valid phone number.");
      setIsAlertVisible(true);
      return;
    }
    setIsSavingConfig(true);

    await postAndHandle("/settings/phone/dni", {
      DNI: currentPhone._id,
      phone_number: currentPhone.phone_number,
      exceptions: currentPhone.exceptions,
      apply_exceptions_on_landing: currentPhone.apply_exceptions_on_landing,
    });

    setIsSavingConfig(false);
    onConfigClose();
  };

  const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phoneNumber);
  };

  // ============================================================
  // PORT IN
  // ============================================================
  const handleSavePortIn = async () => {
    if (!/^PN[0-9a-f]{32}$/.test(phoneNumerToPort)) {
      setAlertMessage("This is not a valid SID for a Twilio phone number.");
      setIsAlertVisible(true);
      return;
    }
    setIsSavingPort(true);
    await postAndHandle("/phone/register-port", {
      sid: phoneNumerToPort,
    });
    setIsSavingPort(false);
    onPortInClose();
  };

  // ============================================================
  // PORT NUMBERS
  // ============================================================
  const getPortInNumbers = async () => {
    const res = await cqAPI.get('/settings/phone/port-in');
    if (res.type === "ok") {
      // The API leaves completed requests out of this list; dropping them here too
      // means the card cannot come back from a cached or older API response.
      const openRequests = res.data.filter(r => !isCompletedPortIn(r));
      setPortInNumberObjs(openRequests);
      setPhoneNumbersToPort((openRequests.map(p => p.phone_number.replace('+1', '')).join(', ')))
      // assigned even when nothing is pending: a port that finishes while the page is
      // open must not leave the number behind as one that still has a port running
      setPendingPortInNumbers(pendingPortInNumbersFrom(res.data));
    }
  }

  useEffect(() => {
    if (isPortInNumbersOpen && portInNumberObjs.length === 0) {
      getPortInNumbers();
    }
  }, [isPortInNumbersOpen]);

  useEffect(() => {
    const getAuthInfo = async () => {
      const numbers = phoneNumbersToPort.replaceAll(' ', '').split(",");
      const res = await cqAPI.post('/settings/phone/port-in/auth', {numbers});
      if (res.type === "ok") {
        setPortInAccountType(res.data?.customer_type || "Business");
        setPortInCustomerName(res.data?.customer_name || "");
        setPortInAuthRepresentative(res.data?.authorized_representative || "");
        setPortInAccountNumber(res.data?.account_number || "");
        setPortInEmail(res.data?.authorized_representative_email || "");
      }
    }
    if (isPortInAuthOpen) {
      getAuthInfo();
    }
  }, [isPortInAuthOpen]);

  const checkPortability = async () => {
    setIsCheckingPortability(true);
    const numbers = phoneNumbersToPort.replaceAll(' ', '').split(",");
    const response = await cqAPI.post("/settings/phone/port-in/check-portability", {numbers})
    setIsCheckingPortability(false);
    if (response.type === "ok") {
      const groupedByCarrier = response.data.reduce((acc, item) => {
        const carrier = item.carrier_name?.toString() || "Unknown Carrier";
        if (!acc[carrier]) acc[carrier] = [];
        acc[carrier].push(item);
        return acc;
      }, {});
      setPortabilityStatuses(groupedByCarrier);
      setTwilioNumbers(response.data.filter(item => item.not_portable_reason === "MANUAL_PORTING_AVAILABLE").map(item => item.number));
      setPortableNumbers(response.data.filter(item => item.portable));
      setPortableNumbersWithPin(response.data.filter(item => item.portable && item.pin_and_account_number_required));
      onPortInNumbersClose();
      onPortInStatusOpen();
    } else {
      setAlertMessage(response.msg);
      setIsAlertVisible(true);
    }
  }

  const checkExistingNumberAndCallerId = () => {
    if (!phoneNumbersData.phones.length) {
      onPortInStatusClose();
      onTempNumberOpen();
    } else {
      onPortInStatusClose();
      if (phoneNumbersData.callerid) {
        // caller id is set, open auth module
        onPortInAuthOpen();
      } else {
        onPortInCallerIdOpen();
      }
    }
  }

  const checkPendingNumbers = async () => {
    const toPortSet = new Set(phoneNumbersToPort);
    const hasCommon = pendingPortInNumbers.length > 0 && phoneNumbersToPort.length > 0 &&
      pendingPortInNumbers.some(n => toPortSet.has(n));
    if (hasCommon) {
      onPortInStatusClose();
      onRetryPortOpen();
    } else {
      // if no number is pending, check if a support ticket should be created for any twilio-to-twilio port-ins
      if (twilioNumbers.length > 0) {
        onPortInStatusClose();
        onCreateSupportTicketOpen();
      } else {// if no pending or twilio numbers, we move to next step (check if user has at least one number)
        checkExistingNumberAndCallerId()
      }
    }
  }

  const cancelPortInReq = async (number) => {
    try {
      const response = await cqAPI.del('/settings/phone/port-in', {number})
      if (response.type !== "ok") {
        setAlertMessage(`Error cancelling porting request: ${response.msg || response.error}`);
        setIsAlertVisible(true);
      } else {
        getPortInNumbers();
      }
    } catch (e) {
      console.error(`Error cancelling porting request: ${e}`);
    }
  }

  const deletePortInReq = async (number) => {
    try {
      const response = await cqAPI.post('/settings/phone/port-in/delete-portin', {number})
      if (response.type !== "ok") {
        setAlertMessage(`Error deleting porting request: ${response.msg || response.error}`);
        setIsAlertVisible(true);
      } else {
        getPortInNumbers();
      }
    } catch (e) {
      console.error(`Error deleting porting request: ${e}`);
    }
  }

  const createTwilioPortingSupportTicket = async () => {
    try {
      // One key per SUBMISSION, reused only while retrying that same submission. The
      // shared client retries POSTs, and without a key a retry opened a second ticket
      // and paged support again about the same numbers.
      //
      // Bound to the numbers, and cleared on success. A key that outlives its request
      // is worse than no key: the component stays mounted, so a later request for
      // different numbers would reuse it, collide on the server's unique index, and be
      // answered with the FIRST request's ticket — silently opening nothing.
      const payloadKey = [...twilioNumbers].sort().join(",");
      if (!portInRequestKeyRef.current || portInRequestKeyRef.current.payload !== payloadKey) {
        portInRequestKeyRef.current = {
          payload: payloadKey,
          id: `portin-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        };
      }

      const response = await cqAPI.post('/settings/phone/port-in/support', {
        numbers: twilioNumbers,
        clientMessageId: portInRequestKeyRef.current.id,
      })
      if (response.type !== "ok") {
        console.error(`Error creating support ticket: ${response.msg || response.error}`);
        setAlertMessage(`Error creating support ticket: ${response.msg || response.error}`);
        setIsAlertVisible(true);
        // Keep the key: the retry is the same request, and should land on the same
        // ticket rather than opening a second one.
        return false;
      }
      portInRequestKeyRef.current = null;
      return true;
    } catch (e) {
      console.error(`Error creating support ticket: ${e}`);
      setAlertMessage(`Error creating support ticket: ${e.message || e}`);
      setIsAlertVisible(true);
      return false;
    }
  }

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setPortInEmail(value);
    if (value && !/^[^ @>)(<#]{1,}@[^.@ ><)(,;]{1,}\.[^@ ><)(,;]+$/.test(value)) {
      setPortInEmailError("Please enter a valid email address");
    } else {
      setPortInEmailError("");
    }
  }

  const handleFileUpload = (uploadedFiles) => {
    setPhoneBills((prevState) => {
      // check file size and type
      const newFiles = uploadedFiles.map(file => {
        const validType = ACCEPTED_FILE_TYPES.includes(file.type);
        const validSize = file.size <= MAX_FILE_SIZE;
        if (!validType) {
          return {
            file,
            status: 'error',
            error: "Invalid file type. Only images and PDFs are allowed."
          }
        }
        if (!validSize) {
          return {
            file,
            status: 'error',
            error: "File too large (max 10MB)."
          }
        }
        return {file, status: 'success'}
      });

      // filter duplicates by name
      const existingNames = new Set(prevState?.map(f => f.name));
      const filteredNew = newFiles.filter(
        f => !existingNames.has(f.file.name),
      );
      if (prevState)
        return [...prevState, ...filteredNew];
      return [...filteredNew];
    });
  }

  const handleSubmitPin = async () => {
    try {
      const response = await cqAPI.put('/settings/phone/port-in', {
        key: 'set_pin',
        data: {
          pins
        }
      });
    } catch (e) {
      console.error(`error submitting pins: ${e}`)
      setAlertMessage(e.msg || e.message || "error submitting pins");
      setIsAlertVisible(true);
    }
  }

  const handleCancelPending = () => {
    cancelPortInReq(currentPendingNum);
    if (currentPendingIdx < portInNumberObjs.length - 1) {
      // ask to cancel next number
      setCurrentPendingIdx(currentPendingIdx + 1);
    } else {
      // move to next module (do they have phone or no)
      onRetryPortInClose();
      if (!phoneNumbersData.phones.length) {
        onTempNumberOpen();
      } else {
        if (phoneNumbersData.callerid) {
          // caller id is set, open auth module
          onPortInAuthOpen();
        } else {
          onPortInCallerIdOpen();
        }
      }
    }
  }

  const handleNextPin = () => {
    if (activePinIdx < portableNumbersWithPin?.length - 1) {
      // skip to get next number pin
      setActivePinIdx(activePinIdx + 1);
    } else {
      // we have all pins, go to next modal (submit)
      handleSubmitPin();
      onPortInPinClose();
      onSubmitPortInOpen();
    }
  }

  const handlePortInAuthSubmit = async () => {
    try {
      const response = await cqAPI.put('/settings/phone/port-in', {
        key: "set_losing_career_info",
        data: {
          numbers: portableNumbers,
          customer_type: portInAccountType,
          customer_name: portInCustomerName,
          account_number: portInAccountNumber,
          authorized_representative: portInAuthRepresentative,
          authorized_representative_email: portInEmail
        }
      });
    } catch (e) {
      console.error(`error updating port in auth data: ${e}`);
      setAlertMessage(e.msg || e.message || "Generic error...");
      setIsAlertVisible(true);
    }
  }

  const handleUploadBills = async () => {
    try {
      const formData = new FormData();
      phoneBills.forEach((fileObj) => {
        if (fileObj.status === 'success') {
          formData.append('files', fileObj.file, encodeURIComponent(fileObj.file.name));
        }
      });
      formData.append('numbers', JSON.stringify(portableNumbers.map(r => r.number)));
      const response = await cqAPI.put('/settings/phone/port-in/upload-bill', formData);
      if (response.type === "ok") {
        // go to next page
        setIsUploading(false);
        onUploadPhoneBillClose();
        onPortInPinOpen();
      }
    } catch (e) {
      console.error(`error updating port in auth data: ${e}`);
      setAlertMessage(e.msg || e.message || "error updating port in auth data");
      setIsAlertVisible(true);
    }
  }

  const handleSubmitPortIn = async () => {
    try {
      const response = await cqAPI.post('/settings/phone/port-in', {
        numbers: portableNumbers.map(p => p.number)
      });
      if (response.type === "ok"){
        onSubmitPortInClose();
        await getPortInNumbers();
      } else {
        setAlertMessage(response.msg || response.error);
        setIsAlertVisible(true);
      }
    } catch (e) {
      console.error(`error submitting port in req: ${e}`);
      setAlertMessage(e.msg || e.message || "error submitting porting request");
      setIsAlertVisible(true);
    }
  }

  const handleContinuePorting = async (portInNum) => {
    setPhoneNumbersToPort(portInNum.phone_number);
    onPortInNumbersOpen();
  }

  // ============================================================
  // VERIFY A NUMBER & POLLING
  // (Now used when user clicks "Add new verified number" inside CallerID modal)
  // ============================================================
  const isValidUsNumber = (phone) => {
    return /^(\+1\d{10}|\d{10})$/.test(phone);
  };

  const areValidNumbers = (numbersString) => {
    const numbers = numbersString?.split(",")
      .map(n=>n.trim())
      .filter(Boolean)
      .map(n => n.replace(/[()\-\s]/g, ''));
    return numbers.length > 0 && numbers.every(isValidUsNumber)
  }

  const handleVerifySubmit = async () => {
    // 1) Post the phone to /settings/phone/verify
    const resp = await cqAPI.post("/settings/phone/verify", {
      phone: verifyPhoneNumber,
    });

    if (resp?.type === "ok") {
      const verificationCode = resp.data?.code;
      onVerifyNumClose();

      // show code modal
      setVerifyPhoneCode(verificationCode || "");
      onVerifyCodeOpen();

      // 2) Start polling
      const intervalId = setInterval(async () => {
        const pollResp = await cqAPI.get(`/settings/phone/verify?callSid=${resp.data.callSid}`);
        if (pollResp?.data === "success") {
          clearInterval(intervalId);
          setPollingIntervalId(null);
          handleCloseVerifyCodeModal();
          toast({
            title: "The number is verified!",
            description: resp.msg,
            status: "success",
          });
          // Refresh the verified phones if CallerID modal is still open
          if (isCallerIdOpen) {
            fetchVerifiedPhones();
          }
          if (isPortInCallerIdOpen) {
            onPortInCallerIdClose();
            onPortInAuthOpen();
          }
        }
      }, 3000);
      setPollingIntervalId(intervalId);
    }
  };

  const handleCloseVerifyCodeModal = () => {
    // Stop polling
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      setPollingIntervalId(null);
    }
    onVerifyCodeCloseInner();
  };

  // ============================================================
  // MENUS & ACTIONS
  // ============================================================
  const performAction = async (action, phone) => {
    switch (action.toLowerCase()) {
      case "release":
        await postAndHandle("/settings/phone/delete", phone);
        break;
      case "make main":
        await postAndHandle("/settings/phone/makemain", {
          _id: phone._id,
        });
        break;
      case "enable location selector":
        await cqAPI.put('/settings/phone/location-selector', { _id: phone._id });
        await fetchPhoneNumbers();
        break;
      case "disable location selector":
        await cqAPI.del('/settings/phone/location-selector', { _id: phone._id });
        await fetchPhoneNumbers();
        break;
      case "setup":
        handleOpenModal(phone);
        break;
      case "change pool size":
        handleOpenModal(phone);
        break;
      case "configure":
        handleConfigOpenModal(phone);
        break;
      default:
        break;
    }
  };

  const handleChange = async (newSelectedOptions, actionMeta, phone) => {
    if (routingUpdateProgress[phone._id]) {
      setAlertMessage("Wait until the previous update is completed.");
      setIsAlertVisible(true);
      return;
    }

    routingUpdateProgress[phone._id] = true;
    const selectedTagsForThisPhone = selectedTags[phone._id];

    setSelectedTags((prev) => ({
      ...prev,
      [phone._id]: newSelectedOptions,
    }));

    await postAndHandle(
      "/settings/phone/numbers/assign",
      {
        ...(phone.isDNI ? { DNI: phone._id } : { _id: phone._id }),
        routing: newSelectedOptions.map((c) => c.value),
      },
      true
    )
      .then((x) => {
        delete routingUpdateProgress[phone._id];
        if (!x) {
          // revert on error
          setSelectedTags((prev) => ({
            ...prev,
            [phone._id]: selectedTagsForThisPhone,
          }));
        }
      })
      .catch((err) => {
        setAlertMessage(err.msg || err.message || "Generic error...");
        setIsAlertVisible(true);
        delete routingUpdateProgress[phone._id];
        setSelectedTags((prev) => ({
          ...prev,
          [phone._id]: selectedTagsForThisPhone,
        }));
      });
  };

  // ============================================================
  // RENDER: DNI + CallerID Modals, etc.
  // ============================================================

  // 1) CallerID Modal
  const CallerIDModal = (
    <Modal isOpen={isCallerIdOpen} onClose={onCallerIdClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Change Outgoing CallerID</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              Below is a list of phone numbers you have verified. You can set
              (or unset) any of these as your outgoing CallerID, or remove them.
            </Text>
            {isLoadingVerifiedPhones ? (
              <Spinner />
            ) : verifiedPhones.length === 0 ? (
              <Text>No verified numbers yet.</Text>
            ) : (
              verifiedPhones.map((item) => (
                <Flex
                  key={item.phone}
                  alignItems="center"
                  justifyContent="space-between"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                  p={2}
                  mb={2}
                >
                  <Text fontWeight="bold" fontSize="lg">
                    {formatPhoneNumber(item.phone)}
                  </Text>
                  <Flex gap={2}>
                    {/* If item.selected is true, show "Unset as callerID", else "Set CallerID" */}
                    <Button
                      size="sm"
                      colorScheme={item.selected ? "red" : "blue"}
                      onClick={() =>
                        handleToggleCallerId(item.phone, item.selected)
                      }
                    >
                      {item.selected ? "Unset as callerID" : "Set CallerID"}
                    </Button>

                    {/* Delete the verified phone altogether */}
                    {item.callSid && (<Button
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDeleteVerifiedPhone(item.phone)}
                    >
                      Delete
                    </Button>)}
                  </Flex>
                </Flex>
              ))
            )}
            {/* Button to add (verify) a new number */}
            <Button
              colorScheme="blue"
              onClick={() => {
                onVerifyNumOpen();
              }}
            >
              Add new verified number
            </Button>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onCallerIdClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  // 1b) Set the Caller ID (CNAM) Modal
  const cnamBlockers = (cnamProfile?.missing || []).filter((entry) =>
    CNAM_ACCOUNT_BLOCKERS.includes(entry)
  );

  const CnamModal = (
    // The form cannot be dismissed mid-submission, so a reopened one is never
    // closed again by the answer to the request that was still in flight.
    <Modal
      isOpen={isCnamOpen}
      onClose={onCnamClose}
      size="xl"
      closeOnOverlayClick={!isSavingCnam}
      closeOnEsc={!isSavingCnam}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Set the Caller ID</ModalHeader>
        <ModalCloseButton isDisabled={isSavingCnam} />
        <ModalBody>
          {isLoadingCnam ? (
            <Spinner />
          ) : cnamLoadFailed || !cnamForm ? (
            <Text>
              We could not load the caller ID information. Please close this
              window and try again.
            </Text>
          ) : (
            <VStack spacing={4} align="stretch">
              <Text>
                {cnamConfirming
                  ? "Please confirm the details below are still correct and submit them to show your practice name on outgoing calls."
                  : "To show your practice name on outgoing calls, we need the details your business is registered under. They have to match the business registered with the EIN or DUNS number below."}
              </Text>

              {cnamBlockers.length > 0 && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    {cnamBlockers.includes("twilio_address_sid")
                      ? "We could not register your practice address with the phone carrier, which the caller ID needs. Please check the address in Settings > Business, then try again."
                      : "This practice is not ready for a caller ID submission yet. Please contact support."}
                  </AlertDescription>
                </Alert>
              )}

              <FormControl isInvalid={cnamMissing.has("legal_name")}>
                <FormLabel>Legal business name</FormLabel>
                <Input
                  placeholder="Exact name on your EIN letter (CP 575)"
                  value={cnamForm.legal_name}
                  onChange={(e) => updateCnamField("legal_name", e.target.value)}
                />
                <FormHelperText>
                  Twilio checks this against your EIN/DUNS registration, so enter it
                  exactly as filed — not a DBA, trade name or abbreviation.
                </FormHelperText>
                <FormErrorMessage>
                  Required, and it has to match the EIN/DUNS registration exactly.
                </FormErrorMessage>
              </FormControl>

              {/* Editable because 15 characters can rarely hold a legal name, so
                  Twilio judges whether the short form is recognizably the same
                  business rather than comparing them literally — a plain
                  truncation is often the worse choice. Confirmed before
                  submitting: after approval, changing it means a new review. */}
              <FormControl>
                <FormLabel>Caller ID name</FormLabel>
                <Input
                  placeholder="What patients see on their phone"
                  maxLength={15}
                  value={cnamForm.cnam_display_name}
                  // Normalized as it is typed so the field always shows exactly
                  // what gets submitted — the API sanitizes on save and can start
                  // the registration in the same request, leaving no later chance
                  // to review a silently rewritten name.
                  onChange={(e) => {
                    setCnamDisplayNameEdited(true);
                    updateCnamField(
                      "cnam_display_name",
                      cnamDisplayNameWhileTyping(e.target.value)
                    );
                  }}
                  // Emptying it goes back to following the legal name, and shows
                  // that value instead of leaving a misleading blank.
                  onBlur={() => {
                    if (cnamForm.cnam_display_name.trim()) {
                      updateCnamField(
                        "cnam_display_name",
                        cnamDisplayNameFrom(cnamForm.cnam_display_name)
                      );
                      return;
                    }
                    setCnamDisplayNameEdited(false);
                    updateCnamField(
                      "cnam_display_name",
                      cnamDisplayNameFrom(cnamForm.legal_name)
                    );
                  }}
                />
                <FormHelperText>
                  Patients see this exact text, up to 15 characters. Twilio has to
                  recognize it as the business named above, so use a shortened form
                  of that name (&quot;Azar Sona Saeidi DDS Inc&quot; &rarr; &quot;SAEIDI
                  DENTAL&quot;). An unrelated brand or DBA is usually rejected.
                  Changing it later means a new Twilio review, so please confirm it
                  now.
                </FormHelperText>
              </FormControl>

              <FormControl isInvalid={cnamMissing.has("business_type")}>
                <FormLabel>Business type</FormLabel>
                <Select
                  placeholder="Select a business type"
                  value={cnamForm.business_type}
                  onChange={(e) => updateCnamField("business_type", e.target.value)}
                >
                  {(cnamProfile?.options?.business_type || []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>Required.</FormErrorMessage>
              </FormControl>

              <HStack spacing={4} align="flex-start">
                <FormControl
                  isInvalid={cnamMissing.has("business_registration_identifier")}
                  maxWidth="160px"
                >
                  <FormLabel>Registered by</FormLabel>
                  <Select
                    placeholder="Select"
                    value={cnamForm.business_registration_identifier}
                    onChange={(e) =>
                      updateCnamField("business_registration_identifier", e.target.value)
                    }
                  >
                    {(cnamProfile?.options?.business_registration_identifier || []).map(
                      (option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      )
                    )}
                  </Select>
                  <FormErrorMessage>Required.</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={cnamMissing.has("business_registration_number")}>
                  <FormLabel>
                    {cnamForm.business_registration_identifier || "EIN/DUNS"} number
                  </FormLabel>
                  <Input
                    placeholder="Registration number"
                    value={cnamForm.business_registration_number}
                    onChange={(e) =>
                      updateCnamField("business_registration_number", e.target.value)
                    }
                  />
                  <FormErrorMessage>Required.</FormErrorMessage>
                </FormControl>
              </HStack>

              <FormControl isInvalid={cnamMissing.has("website_url")}>
                <FormLabel>Website</FormLabel>
                <Input
                  placeholder="https://www.example.com"
                  value={cnamForm.website_url}
                  onChange={(e) => updateCnamField("website_url", e.target.value)}
                />
                <FormErrorMessage>
                  Required, and it has to start with http:// or https://.
                </FormErrorMessage>
              </FormControl>

              <Text fontWeight="bold" pt={2}>
                Authorized representative
              </Text>

              <HStack spacing={4} align="flex-start">
                <FormControl isInvalid={cnamMissing.has("representative.first_name")}>
                  <FormLabel>First name</FormLabel>
                  <Input
                    value={cnamForm.representative.first_name}
                    onChange={(e) =>
                      updateCnamRepresentative("first_name", e.target.value)
                    }
                  />
                  <FormErrorMessage>Required.</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={cnamMissing.has("representative.last_name")}>
                  <FormLabel>Last name</FormLabel>
                  <Input
                    value={cnamForm.representative.last_name}
                    onChange={(e) =>
                      updateCnamRepresentative("last_name", e.target.value)
                    }
                  />
                  <FormErrorMessage>Required.</FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack spacing={4} align="flex-start">
                <FormControl isInvalid={cnamMissing.has("representative.email")}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    value={cnamForm.representative.email}
                    onChange={(e) => updateCnamRepresentative("email", e.target.value)}
                  />
                  <FormErrorMessage>A valid email is required.</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={cnamMissing.has("representative.phone_number")}>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    value={cnamForm.representative.phone_number}
                    onChange={(e) =>
                      updateCnamRepresentative("phone_number", e.target.value)
                    }
                  />
                  <FormErrorMessage>A reachable phone number is required.</FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack spacing={4} align="flex-start">
                <FormControl isInvalid={cnamMissing.has("representative.business_title")}>
                  <FormLabel>Business title</FormLabel>
                  <Input
                    placeholder="e.g. Owner"
                    value={cnamForm.representative.business_title}
                    onChange={(e) =>
                      updateCnamRepresentative("business_title", e.target.value)
                    }
                  />
                  <FormErrorMessage>Required.</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={cnamMissing.has("representative.job_position")}>
                  <FormLabel>Job position</FormLabel>
                  <Select
                    placeholder="Select a job position"
                    value={cnamForm.representative.job_position}
                    onChange={(e) =>
                      updateCnamRepresentative("job_position", e.target.value)
                    }
                  >
                    {(cnamProfile?.options?.job_position || []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>Required.</FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onCnamClose}
            isDisabled={isSavingCnam}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isLoading={isSavingCnam}
            isDisabled={isLoadingCnam || !cnamForm}
            onClick={handleSaveCnamProfile}
          >
            {cnamConfirming ? "Confirm & Submit" : "Submit"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const DNIModal = (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>DNI</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={4}>
              Dynamic Number Insertion (DNI) is a call tracking feature where a
              unique phone number is dynamically tied to each website traffic
              source. We forward all incoming calls to your main number while
              keeping track of all calls.
            </Text>
            {(!currentPhone || !currentPhone.areacode) && (
              <Text textAlign="left" mb={4}>
                Please enter a 3-digit area code.
              </Text>
            )}
            {(!currentPhone || !currentPhone.areacode) && (
              <Input
                placeholder="Area Code"
                onChange={(e) => setCurrentPhoneAreaCode(e.target.value)}
                maxLength={3}
                width="100%"
              />
            )}
            <NumberInput
              min={1}
              max={10}
              value={poolSize}
              onChange={handlePoolSizeChange}
              width="100%"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={() => savePoolSize(false)}>
            Save
            {isSavingPoolSize && <Spinner size="sm" ml="2" />}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const DNIConfigModal = (
    <Modal isOpen={isConfigOpen} onClose={onConfigClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Configure DNI</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl id="phone-number-to-replace">
              <FormLabel textAlign="left">Phone Number to Replace</FormLabel>
              <Input
                placeholder="Enter phone number"
                value={currentPhone && currentPhone.phone_number}
                onChange={(e) =>
                  setCurrentPhone({
                    ...currentPhone,
                    phone_number: e.target.value,
                  })
                }
              />
              <Text fontSize="sm" textAlign="left">
                Format: International (e.g., +1234567890) or national.
              </Text>
            </FormControl>
            <FormControl id="keywords">
              <FormLabel textAlign="left">Keywords</FormLabel>
              <Input
                placeholder="Do not replace if the url includes any of these keywords (comma separated)"
                value={currentPhone && currentPhone.exceptions}
                onChange={(e) =>
                  setCurrentPhone({
                    ...currentPhone,
                    exceptions: e.target.value,
                  })
                }
              />
            </FormControl>
            <Checkbox
              isChecked={
                currentPhone && currentPhone.apply_exceptions_on_landing
              }
              onChange={(e) =>
                setCurrentPhone({
                  ...currentPhone,
                  apply_exceptions_on_landing: e.target.checked,
                })
              }
              textAlign="left"
            >
              Apply on landing page
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={handleSaveConfig}>
            Save
            {isSavingConfig && <Spinner size="sm" ml="2" />}
          </Button>
          <Button variant="ghost" onClick={onConfigClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const BuyNumberModal = (
    <Modal isOpen={isBuyNumOpen} onClose={onBuyNumClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Phone Number</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={4}>
              The new phone numbers can be used to track phone calls from a
              specific source like Yelp or Google. We forward all incoming calls
              to your main number while keeping track of calls.
            </Text>
            <Input
              placeholder="Area Code"
              onChange={(e) => setCurrentPhoneAreaCode(e.target.value)}
              maxLength={3}
              width="100%"
            />
            <Input
              placeholder="Source"
              onChange={(e) => setCurrentCampaign(e.target.value)}
              maxLength={40}
              value={currentCampaign}
              width="100%"
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={() => savePoolSize(true)}>
            Save
            {isSavingPoolSize && <Spinner size="sm" ml="2" />}
          </Button>
          <Button variant="ghost" onClick={onBuyNumClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const PortInModal = (
    <Modal isOpen={isPortInOpen} onClose={onPortInClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Register a Ported Number</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={4}>
              Make sure that you have submitted the port for this number in
              Twilio. Once submitted, get the SID from there and register it
              here:
            </Text>
            <FormControl id="phone-number-to-replace">
              <FormLabel textAlign="left">Phone Number SID</FormLabel>
              <Input
                placeholder="Enter phone number's SID"
                value={phoneNumerToPort}
                onChange={(e) => setPhoneNumerToPort(e.target.value)}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={handleSavePortIn}>
            Save
            {isSavingPort && <Spinner size="sm" ml="2" />}
          </Button>
          <Button variant="ghost" onClick={onPortInClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const PortNumberModal = (
    <Modal isOpen={isPortInNumbersOpen} onClose={onPortInNumbersClose} size={"2xl"}>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader textAlign="left">Port Numbers</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={4}>
              Transfer your existing phone numbers to our provider to access all features.
              <br/>
              *Toll-free numbers and numbers outside the US cannot be ported automatically.
            </Text>
            <FormControl id="numbers-to-port" position={"relative"}>
              <Textarea
                height={"200px"}
                placeholder="Enter phone numbers here, separated by comas."
                value={phoneNumbersToPort}
                onChange={(e) => {
                  setIsCheckingPortability(false);
                  setPhoneNumbersToPort(e.target.value);
                }}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button mr={3} onClick={checkPortability} isDisabled={!areValidNumbers(phoneNumbersToPort)}>
            Check{isCheckingPortability && "ing"} Portability
            {isCheckingPortability && <Spinner size="sm" ml="2" />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const PortabilityStatusModal = (
    <Modal isOpen={isPortInStatusOpen} onClose={onPortInStatusClose} size={'2xl'} >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Port Numbers</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box
              overflowY="auto"
              height="500px"
              p={4}
              borderRadius="md"
              borderWidth="1px"
              whiteSpace="pre-wrap"
            >
              {Object.entries(portabilityStatuses).map(([carrier, numbers]) => {

                const portable = numbers.filter(n => n.portable);
                const notPortable = numbers.filter(n => !n.portable);

                return (
                  <Box key={carrier} mb={6}>
                    <Text mb={2}>
                      These numbers are associated with losing carrier: {' '}
                      <Text as="span" fontWeight="bold">{carrier}</Text>
                    </Text>

                    {portable.length > 0 && (
                      <Text pl={4} mb={2}>
                        You can port the following numbers:
                        <br />
                        {portable.map(n => n.number.replace(/^\+1/, '')).join(', ')}
                      </Text>
                    )}

                    {notPortable.length > 0 && (
                      <>
                        <Text pl={4}>The following numbers cannot be ported:</Text>
                        {Object.entries(
                          notPortable.reduce((acc, curr) => {
                            const reason = curr.not_portable_msg || curr.msg || "Unknown Error";
                            if (!acc[reason]) acc[reason] = [];
                            acc[reason].push(curr.number.replace(/^\+1/, ''));
                            return acc;
                          }, {})
                        ).map(([reason, nums]) => (
                          <Box key={reason} pl={8} mb={3}>
                            <Text fontStyle="italic">{reason}</Text>
                            <Text>{nums.join(', ')}</Text>
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>

          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button mr={3} onClick={() => {
            onPortInStatusClose();
            onPortInNumbersOpen();
          }} variant={"ghost"}>Back</Button>
          <Button mr={3} isDisabled={!portableNumbers.length && !twilioNumbers.length} onClick={() => {
            checkPendingNumbers();
          }}>Next</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const RetryPortInModal = (
    <Modal isOpen={isRetryPortInOpen} onClose={onRetryPortInClose} size={'md'} >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Submit Porting Request Again</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={10}>
              <Text as={"span"} fontWeight="bold">{currentPendingNum}</Text>
              {` is currently being ported. Cancel the existing request and start over?`}
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button mr={3} onClick={() => {
            if (currentPendingIdx > 0) {
              setCurrentPendingIdx(currentPendingIdx - 1);
            } else {
              onRetryPortInClose();
              onPortInStatusOpen();
            }
          }} variant={"ghost"}>Back</Button>
          <Button mr={3} onClick={() => {
            handleCancelPending();
          }}>Submit</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const createSupportTicketModal = (
    <Modal isOpen={isCreateSupportTicketOpen} onClose={onCreateSupportTicketClose} size={'md'} >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign='left'>Create Customer Support Ticket</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={10}>
              <Text as={"span"} fontWeight="bold">{twilioNumbers.toString()}</Text>
              {` can not be ported automatically. Would you like to create a customer support ticket to handle this?`}
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button mr={3} onClick={() => {
            onCreateSupportTicketClose();
            onPortInStatusOpen();
          }} variant={"ghost"}>Back</Button>
          <Button
            mr={3}
            isLoading={submittingPortInTicket}
            // Awaited, and the button held, so a second press cannot start a second
            // request. The server keys on `clientMessageId` as well — both, because
            // this stops the duplicate and that survives a genuine network retry.
            onClick={async () => {
              if (submittingPortInTicket) return;
              setSubmittingPortInTicket(true);
              let opened = false;
              try {
                opened = await createTwilioPortingSupportTicket();
              } finally {
                setSubmittingPortInTicket(false);
              }
              // Only advance when a ticket actually exists. Closing regardless left the
              // customer believing their porting request had been submitted when a
              // network or database failure meant nothing was opened at all — and the
              // modal, with their numbers in it, was gone.
              if (!opened) return;
              onCreateSupportTicketClose();
              if (portableNumbers.length > 0){
                checkExistingNumberAndCallerId();
              }
            }}
          >Submit</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const TemporaryNumberModal = (
    <Modal isOpen={isTempNumberOpen} onClose={onTempNumberClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Phone Number</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={4}>
              You don't have a number on our platform yet. Please create a temporary number
              while your current phone numbers are being ported.
              <br/><br/>
              Once your temporary number is set up, we'll configure your caller ID to display
              one of the numbers you're transferring.
              <br/><br/>
              Please enter a 3-digit area code.
            </Text>
            <Input
              placeholder="Area Code"
              onChange={(e) => setCurrentPhoneAreaCode(e.target.value)}
              maxLength={3}
              width="100%"
            />
            <Input
              placeholder="Source or Description"
              onChange={(e) => setCurrentCampaign(e.target.value)}
              maxLength={40}
              value={currentCampaign}
              width="100%"
            />
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button variant="ghost" onClick={() => {
            onTempNumberClose();
            onPortInStatusOpen();
          }}>Back</Button>
          <Button mr={3} onClick={() => {
            setPoolSize(1);
            savePoolSize(true);
          }}>
            Next
            {isSavingPoolSize && <Spinner size="sm" ml="2" />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const PortInCallerIdModal = (
    <Modal isOpen={isPortInCallerIdOpen} onClose={onPortInCallerIdClose} size={'xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Port Numbers</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              Select the number you want as your outgoing caller ID
              (this is the number your patients will see when you call or text them):
            </Text>
            <RadioGroup mb={10} ml={5} value={verifyPhoneNumber} onChange={setVerifyPhoneNumber}>
              <Stack spacing={4}>
                {portableNumbers.map(item => (
                  <Radio key={item.number} value={item.number}>
                    {formatPhoneNumber(item.number)}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button variant="ghost" onClick={() => {
            onPortInCallerIdClose();
            onPortInStatusOpen();
          }}>Back</Button>
          <Button mr={3} onClick={() => {
            const isVerified = verifiedPhones.some(item => item.phone === verifyPhoneNumber.replace('+1', ''));
            if (!isVerified) {
              handleVerifySubmit();
              onVerifyCodeOpen();
            } else {
              if (verifyPhoneNumber.replace('+1', '') !== phoneNumbersData.callerid) {
                handleToggleCallerId(verifyPhoneNumber.replace('+1', ''), false);
              }
              onPortInCallerIdClose();
              onPortInAuthOpen();
            }
          }}>Next</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const PortInAuthModal = (
    <Modal isOpen={isPortInAuthOpen} onClose={onPortInAuthClose} size={'2xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Porting Authorization</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch"></VStack>
          <Text>
            Please provide all the information requested below to port <Text as={"span"} fontWeight="bold">{portableNumbers.map(r=>r.number).toString()}</Text>.
          </Text>
          <Flex ml={10} mt={4} direction="column">
            <FormControl>
              <FormLabel>Account Type</FormLabel>
              <RadioGroup ml={10} value={portInAccountType} onChange={setPortInAccountType}>
                <Stack >
                  <Radio id='accountType-business' value={'Business'}>
                    Business
                  </Radio>
                  <Radio id='accountType-Individual'  value={'Individual'}>
                    Individual
                  </Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            <FormControl mt={4} mb={4} direction="column">
              <FormLabel>Customer Name (can be business or individual name)</FormLabel>
              <Input
                placeholder="Name as it is registered with the losing carrier"
                value={portInCustomerName}
                onChange={(e) => setPortInCustomerName(e.target.value)}
                width={500}
                />
            </FormControl>

            <FormControl mt={4} mb={4} direction="column">
              <FormLabel>Authorized Represantative Name (first and last)</FormLabel>
              <Input
                placeholder="Authorized person with losing carrier to make changes"
                value={portInAuthRepresentative}
                onChange={e => setPortInAuthRepresentative(e.target.value)}
                width={500}
                />
            </FormControl>

            <FormControl mt={4} mb={4} direction="column" isInvalid={!!portInEmailError}>
              <FormLabel>Email</FormLabel>
              <Input
                placeholder="Authorized person's email that will also receive documents"
                value={portInEmail}
                onChange={handleEmailChange}
                width={500}
                />
              <FormErrorMessage>{portInEmailError}</FormErrorMessage>
            </FormControl>

            <FormControl mt={4} mb={4} direction="column">
              <FormLabel>Customer Account Number</FormLabel>
              <Input
                placeholder="Customer account number in the losing carrier"
                value={portInAccountNumber}
                onChange={e => setPortInAccountNumber(e.target.value)}
                width={500}
              />
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter mt={6} mb={5}>
          <Button  mr={3} variant="ghost" onClick={() => {
            onPortInAuthClose();
            if (phoneNumbersData.callerid) {
              // caller id is set, open status module
              onPortInStatusOpen();
            } else {
              onPortInCallerIdOpen();
            }
          }}>Back</Button>
          <Button
            mr={3}
            isDisabled={!(portInAccountType && portInCustomerName && portInAuthRepresentative && portInAccountNumber && portInEmail && !portInEmailError)}
            onClick={() => {
              onPortInAuthClose();
              handlePortInAuthSubmit();
              onUploadPhoneBillOpen();
            }}
          >Next</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )

  const UploadPhoneBillsModal = (
    <Modal isOpen={isUploadPhoneBillOpen} onClose={onUploadPhoneBillClose} size={'2xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Upload Phone Bills</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={10}>
              The phone bill should be the most recent bill you have, within the last 30 days, that includes the
              account, owner/authorized user name, and address information for all the numbers you'd like to port.
              <br/>
              <br/>
              File must be image or pdf (max size 10MB).
            </Text>

            {phoneBills && phoneBills.length > 0 && (
              <Box mb={10}>
                {phoneBills.map((fileObj, idx) => (
                  <HStack key={idx}>
                    <Text>{fileObj.file.name}</Text>
                    {fileObj.status === "success" ? (
                      <Icon as={IoMdCheckmark} color={"green.400"}/>
                    ) : (
                      <>
                        <Icon as={PiWarningCircleBold} color={"red.500"}/>
                        <Text color={"red.500"}>{fileObj.error}</Text>
                      </>
                    )}
                    <DeleteIcon onClick={() =>
                      setPhoneBills((prevState) => prevState.filter(f => f.file.name !== fileObj.file.name))}
                    />
                  </HStack>
                  ))}
              </Box>
            )}

            <Box
              border={"1px dashed"}
              borderColor={"lightgray"}
              borderRadius={"md"}
              py={8}
              alignContent={"center"}
              textAlign={"center"}
              position={"relative"}
              cursor={"pointer"}
              _hover={{borderColor: "grey.400"}}
              onClick={() => fileInputRef.current.click()}
              height={150}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const files = Array.from(e.dataTransfer?.files) ?? undefined;
                if (files)
                  handleFileUpload(files);
                  e.dataTransfer.clearData();
                }
              }
              >
              <HStack spacing={2} align="center" justify="center">
                <Icon as={RiUploadCloud2Line}  color={"grey.400"} w={8} h={8}/>
                <Text>Drop files to upload or </Text>
                <Button
                  variant={"outline"}
                  onClick={e => {
                    e.stopPropagation();
                    fileInputRef.current.click();
                  }}
                >Browse</Button>
                <Input
                  ref={fileInputRef}
                  type="file"
                  multiple={true}
                  display={"none"}
                  accept={"image/*,application/pdf"}
                  onChange={e => handleFileUpload(Array.from(e.target.files))}
                  />
              </HStack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter mt={6} mb={5}>
          <Button  mr={3} variant="ghost" onClick={() => {
            onUploadPhoneBillClose();
            onPortInAuthOpen();
          }}>Back</Button>
          <Button
            mr={3}
            isDisabled={!phoneBills?.length || phoneBills?.some(f => f.status === "error")}
            onClick={() => {
              setIsUploading(true)
              handleUploadBills();
            }}
          >
            Next
            {isUploading && <Spinner size="sm" ml="2" />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )

  const PortInPinModal = (
    <Modal isOpen={isPortInPinOpen} onClose={onPortInPinClose} size={"2xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Port Numbers</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <VStack spacing={4}>
            <Text >
              <Text as={"span"} fontWeight={"bold"}>{activePinPhone?.number?.replace('+1', '')}</Text>
              {" "}
              associated with losing carrier,
              {" "}
              <Text as={"span"} fontWeight={"bold"}>{activePinPhone?.carrier_name}</Text>
              , requires a pin number to port. If you do not know your pin number, please contact
              {" "}
              <Text as={"span"} fontWeight={"bold"}>{activePinPhone?.carrier_name}</Text>
              {" "}
              to obtain a pin number.
              <br/><br/><br/><br/>
              Provide the pin below and submit:
            </Text>
            <Input
              ml={3}
              value={pins[activePinPhone?.number] || ""}
              onChange={(e) => {
                setPins({...pins, [activePinPhone?.number]: e.target.value});
              }}
              />
          </VStack>
        </ModalBody>
        <ModalFooter as={HStack} mb={5}>
          <Button variant={"ghost"} ml={3} alignSelf={"left"} onClick={() => {
            if (activePinIdx > 0) {
              // go to previous number pin
              setActivePinIdx(activePinIdx - 1);
            } else {
              // go to previous modal
              onPortInPinClose();
              onUploadPhoneBillOpen();
            }
          }}>Back</Button>
          <Spacer />
          <Button variant={"outline"} border={"2px"} ml={3} align={"flex-end"} onClick={handleNextPin}>Add Later</Button>
          <Button isDisabled={!pins[activePinPhone?.number]} ml={3} align={"flex-end"} onClick={handleNextPin}>Next</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )

  const SubmitPortInModal = (
    <Modal isOpen={isSubmitPortInOpen} onClose={onSubmitPortInClose}  size={"2xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Submitting Porting Request</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={2} mb={20}>
            <Text>Submit porting request to carrier? If you need to make any changes press back.</Text>
          </VStack>
        </ModalBody>
        <ModalFooter mb={5}>
          <Button variant={"ghost"} mr={3} onClick={() => {
            onSubmitPortInClose();
            onPortInPinOpen();
          }}>Back</Button>
          <Button onClick={handleSubmitPortIn} mr={3} >Submit</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )

  // The modals for verifying a new number
  const VerifyNumberModal = (
    <Modal isOpen={isVerifyNumOpen} onClose={onVerifyNumClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Verify a New Phone Number</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              Enter a US phone number to verify. Use the format +1XXXXXXXXXX or
              a 10-digit number.
            </Text>
            <FormControl>
              <FormLabel>Phone Number</FormLabel>
              <Input
                placeholder="e.g., +14155552671 or 4155552671"
                value={verifyPhoneNumber}
                onChange={(e) => setVerifyPhoneNumber(e.target.value)}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onVerifyNumClose}>
            Cancel
          </Button>
          <Button
            isDisabled={!isValidUsNumber(verifyPhoneNumber)}
            onClick={handleVerifySubmit}
            colorScheme="blue"
          >
            Verify
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const VerifyCodeModal = (
    <Modal isOpen={isVerifyCodeOpen} onClose={handleCloseVerifyCodeModal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Verify Your Phone</ModalHeader>
        <ModalCloseButton onClick={handleCloseVerifyCodeModal} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              We initiated a call to <b>{verifyPhoneNumber}</b>.
              Please enter the following code when prompted:
            </Text>
            <Heading size="lg" textAlign="center">
              {verifyPhoneCode}
            </Heading>
            <Text>
              This window will automatically close once verification is
              confirmed, or you may close it manually to stop polling.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleCloseVerifyCodeModal}>
            Back
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <Box className="phone-numbers-div" p={4}>
      {/* CallerID modal */}
      {CallerIDModal}

      {/* Set the Caller ID (CNAM) modal */}
      {CnamModal}

      {/* Existing modals */}
      {DNIModal}
      {DNIConfigModal}
      {BuyNumberModal}
      {PortInModal}
      {PortNumberModal}
      {PortabilityStatusModal}
      {RetryPortInModal}
      {createSupportTicketModal}
      {TemporaryNumberModal}
      {PortInCallerIdModal}
      {PortInAuthModal}
      {UploadPhoneBillsModal}
      {PortInPinModal}
      {SubmitPortInModal}
      {VerifyNumberModal}
      {VerifyCodeModal}

      {/* Alerts */}
      {isAlertVisible && (
        <Box position="fixed" top="0" left="0" right="0" zIndex="1500">
          <Alert status="error" bg="red.500">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription display="block">{alertMessage}</AlertDescription>
            </Box>
            <CloseButton
              position="absolute"
              right="8px"
              top="8px"
              onClick={() => setIsAlertVisible(false)}
            />
          </Alert>
        </Box>
      )}

  <Flex alignItems="center" mb={4} justifyContent="space-between">
    <Box flex="1" textAlign="center" fontWeight="bold">
      {phoneNumbersData.callerid? `The outgoing caller ID is set to ${formatPhoneNumber(phoneNumbersData.callerid)}` : ''}
    </Box>

    <Menu>
      <MenuButton as={Button} rightIcon={<FontAwesomeIcon icon={faChevronDown} />}>
        Actions
      </MenuButton>
      <MenuList>
        <MenuItem onClick={handleOpenBuyNumModal}>Buy a Number</MenuItem>
        <MenuItem onClick={() => handleOpenModal({})}>Buy DNI</MenuItem>
        {Auth.user && (Auth.user.isSuperAdmin || Auth.user.isAdmin) && (
          <MenuItem onClick={onPortInNumbersOpen}>Port Number(s)</MenuItem>
        )}
        {Auth.user && Auth.user.isSuperAdmin && (
          <MenuItem onClick={onPortInOpen}>Register a Ported Number</MenuItem>
        )}
        <MenuItem onClick={onCallerIdOpen}>Change outgoing CallerID</MenuItem>
        {/* The caller ID name belongs to the practice, not to one number: it is
            submitted once here, and changing the main number just re-points it.
            Once set it stays visible but disabled, showing the registered name. */}
        <MenuItem
          onClick={() => {
            // cleared in the same update as the open, so the previous visit's
            // answers cannot show for a frame before the reload replaces them
            resetCnamForm();
            onCnamOpen();
          }}
          isDisabled={phoneNumbersData.cnam?.submitted === true}
          title={
            phoneNumbersData.cnam?.submitted
              ? `The caller ID is already set to ${phoneNumbersData.cnam?.caller_id_name || ""}`
              : undefined
          }
        >
          {phoneNumbersData.cnam?.submitted
            ? `Caller ID name: ${phoneNumbersData.cnam?.caller_id_name || "submitted"}`
            : "Set the Caller ID"}
        </MenuItem>
      </MenuList>
    </Menu>
  </Flex>

      {/* Phone Cards */}
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3 }}
        spacing="1rem"
        className="simple-grid"
        minHeight="50dvh"
      >
        {phoneNumbers?.map((phone, index) => (
          <Box
            key={index}
            className="phone-card"
            id="phone-card"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            p={4}
            bg={backgroundColor}
            boxShadow="base"
          >
            <Box
              className="card-header"
              id="card-header"
              p={4}
              borderTopRadius="lg"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              bg={headerBackgroundColor}
            >
              <Heading size="md" color={headerTextColor} id="phone-number">
                {formatPhoneNumber(phone.title)}
              </Heading>
              <Menu id={`header-menu-${phone._id}`}>
                <MenuButton
                  as={IconButton}
                  icon={<FontAwesomeIcon icon={faEllipsisV} />}
                  backgroundColor={headerBackgroundColor}
                  _hover={{ bg: hoverBackgroundColor, color: backgroundColor }}
                  variant="ghost"
                />
                <MenuList>
                  {phone.actions.map((action, actionIndex) => (
                    <MenuItem
                      key={actionIndex}
                      onClick={() => performAction(action, phone)}
                    >
                      {action}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>

            <Flex className="card-body" flexDirection="column" flex="1" p={4}>
              <Stack spacing={3} textAlign="left" flex="1" mb={5} mt={3}>
                <Flex alignItems="center" className="flex-item">
                  <EditableText
                    initialValue={phone.name}
                    onSave={(value) => handleSaveClick(phone._id, value)}
                    placeholder="Name"
                    editable={!phone.main_number}
                    className="card-header"
                  />
                </Flex>
                {defaultRouting?.name && (
                  <Flex alignItems="center" className="flex-item">
                    <Text minWidth="120px" mr={2}>
                      Default routing plan:
                    </Text>
                    <Text as="b">{defaultRouting?.name}</Text>
                  </Flex>
                )}
                <Flex alignItems="center" className="flex-item">
                  <Text minWidth="120px" mr={2}>
                    Calls:
                  </Text>
                  <Text as="b">
                    {phone.calls.count ? phone.calls.count : 0}
                  </Text>
                </Flex>
                {phone.calls.avg_duration && (
                  <Flex alignItems="center" className="flex-item">
                    <Text minWidth="120px" mr={2}>
                      Avg Duration:
                    </Text>
                    <Text as="b">{Math.ceil(phone.calls.avg_duration)}</Text>
                  </Flex>
                )}
                {phone.isDNI && (
                  <Flex alignItems="center" className="flex-item">
                    <Text minWidth="120px" mr={2}>
                      Pool Size:
                    </Text>
                    <Text>{Math.ceil(phone.size)}</Text>
                  </Flex>
                )}
                <Flex alignItems="center" className="flex-item">
                  <Text minWidth="120px" mr={2}>
                    Last Call:
                  </Text>
                  <Text as="b">
                    {phone.calls.last_call
                      ? moment(phone.calls.last_call).calendar()
                      : "No calls"}
                  </Text>
                </Flex>

                <Spacer />
                {!phone.router ? (<>
                <Text as="b">Incoming Routing Plan</Text>
                <div id={`routing-plans-select-${phone._id}`}>
                  <ReactSelect
                    styles={customStyles}
                    isMulti
                    options={phoneNumbersData.routings.map((option) => ({
                      value: option._id,
                      label: option.name,
                    }))}
                    value={selectedTags[phone._id] || []}
                    onChange={(selectedOptions) =>
                      handleChange(selectedOptions, {}, phone)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    components={{
                      DropdownIndicator: props => {
                        const { innerProps, children } = props;
                        return (
                          <components.DropdownIndicator
                            {...props}
                            innerProps={{
                              ...innerProps,
                              id: `dropdown-button-${phone._id}`,
                            }}
                          >
                            {children}
                          </components.DropdownIndicator>
                        );
                      },
                      Option: props => {
                        const { innerProps, data, children } = props;
                        return (
                          <components.Option
                            {...props}
                            innerProps={{
                              ...innerProps,
                              id: `option-${phone._id}-${data.value}`,
                            }}
                          >
                            {children}
                          </components.Option>
                        );
                      },
                      MultiValue: props => {
                        const { innerProps, data, children } = props;
                        return (
                          <components.MultiValue
                            {...props}
                            innerProps={{
                              ...innerProps,
                              id: `capsule-${phone._id}-${data.value}`,
                            }}
                          >
                            {children}
                          </components.MultiValue>
                        );
                      },
                      MultiValueRemove: props => {
                        const { innerProps, data, children } = props;
                        return (
                          <components.MultiValueRemove
                            {...props}
                            innerProps={{
                              ...innerProps,
                              id: `remove-${phone._id}-${data.value}`,
                            }}
                          >
                            {children}
                          </components.MultiValueRemove>
                        );
                      }
                    }}
                  />
                </div>
                </>) : (
                  <>
                  <Text as="b" textAlign="center">Location Selector</Text>
                  <div />
                  </>
                )}
              </Stack>
            </Flex>
          </Box>
        ))}
        {portInNumberObjs?.map((portInNum, index) => (
          <Box
            key={index}
            className="phone-card"
            id="portin-card"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            p={4}
            bg={backgroundColor}
            boxShadow="base"
          >
            <Box
              className="card-header"
              id="card-header"
              p={4}
              borderTopRadius="lg"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              bg={headerBackgroundColor}
            >
              <Heading size="md" color={headerTextColor} id="phone-number">
                {formatPhoneNumber(portInNum.phone_number)}
              </Heading>
              <Menu id={`header-menu-${portInNum._id}`}>
                <MenuButton
                  as={IconButton}
                  icon={<FontAwesomeIcon icon={faEllipsisV} />}
                  backgroundColor={headerBackgroundColor}
                  _hover={{ bg: hoverBackgroundColor, color: backgroundColor }}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem
                    key={`cancel-${portInNum._id}`}
                    onClick={() => cancelPortInReq(portInNum.phone_number)}
                    isDisabled={['Canceling', 'Canceled', ''].includes(portInNum.status)}
                  >
                    Cancel Port
                  </MenuItem>
                  <MenuItem
                    key={`delete-${portInNum._id}`}
                    onClick={() => deletePortInReq(portInNum.phone_number)}
                    isDisabled={!['Canceling', 'Canceled', ''].includes(portInNum.status) && portInNum.portable}
                  >
                    Delete Port In
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>

            <Flex className="card-body" flexDirection="column" flex="1" p={4}>
              <Stack spacing={3} textAlign="left" flex="1" mb={5} mt={3}>
                {portInNum.portable ?
                  <Flex alignItems="center" className="flex-item">
                    <Text minWidth="120px" mr={2}>
                      Porting Status:
                    </Text>
                    <Text as="b">
                      {portInNum.status || "Incomplete Request"}
                    </Text>
                  </Flex> :
                  <Flex alignItems="center" className="flex-item">
                    <Text minWidth="120px">
                      This number is not portable
                    </Text>
                    <Text as="b">
                      {portInNum.not_portable_reason_code}: {portInNum.not_portable_msg}
                    </Text>
                  </Flex>
                }
                {portInNum.rejection_reason &&
                  <Flex alignItems="center" className="flex-item">
                    <Text minWidth="120px" mr={2}>
                      Porting rejected:
                    </Text>
                    <Text as="b">
                      {portInNum.rejection_reason_code}: {portInNum.rejection_reason}
                    </Text>
                  </Flex>
                }
                <Spacer/>
                {!portInNum.status && // means the number porting wasn't completed
                  <Flex align="center" justify={"center"} >
                    <Button w={150} onClick={() => handleContinuePorting(portInNum)}>Continue</Button>
                  </Flex>
                }
                <Spacer/>

              </Stack>
            </Flex>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

export default PhoneNumbers;
